<template>
  <Draggable>
    <div
      @click="setActive"
      class="drag-item"
      :class="classes"
    >
      <div class="drag-zone">
        <img class="drag-icon" :src="dragIcon">
      </div>
      <div class="content-zone">
        <p
          v-if="size === 'small'" class="truncate-text"
        >
          {{ name }}
        </p>
        <div
          v-if="size === 'big'"
        >
          <div class="form-container">
            <!-- CONFIRMATION FIELDS -->
            <div
              v-if="item.element_type === 'confirmation'"
            >
              <div
                class="checkbox-container no-margin"
              >
                <input type="checkbox">
                <label> {{ name }} </label>
              </div>
            </div>

            <label
              v-else
              :class="labelClasses(item)"
            >
              <div v-if="name">{{ name }}</div>
              <div v-else><i>{{ $t('ui.drag_editor.no_label') }}</i></div>
              <sensual-badge
                v-if="item.hidden"
                color="grey-dark"
                :text="$t('ui.drag_editor.badges.hidden')"
              />
            </label>
            <!-- ANY TYPE OF TEXT FIELD -->
            <div v-if="item.element_type === 'text'">
              <input
                readonly
                type="text"
                v-bind="textBindings"
              >
            </div>
            <!-- ANY TYPE OF SELECT FIELD -->
            <div v-else-if="item.element_type === 'select'">
              <select>
                <div v-if="defaultValueSelect">
                  <option>{{ defaultValueSelect }}</option>
                </div>
              </select>
            </div>
            <!-- Country SELECT FIELD -->
            <div v-else-if="item.element_type === 'country_select'">
              <select
                v-model="defaultValueSelectCountry"
              >
                <option v-for="country in countries" :value="country['value']">{{ country['name'] }}</option>
              </select>
            </div>
            <!-- ANY TYPE OF TEXTAREA FIELD -->
            <div v-else-if="item.element_type === 'textarea'">
              <textarea
                v-bind="textBindings"
              />
            </div>
            <!-- RADIO WITH VALUES -->
            <div v-else-if="item.element_type.includes('radio') && item.allowed_values.length > 0">
              <div
                class="radio-container"
                v-for="value in shownAllowedValues"
              >

                <div v-if="item.show_as_buttons" class="custom_select">
                  {{ value.name }}
                </div>

                <div v-else>
                  <input type="radio">
                  <label
                    :class="{'checked': isSelected(value)}"
                  >{{ value.name }}</label>
                </div>


              </div>
              <div
                v-if="item.allowed_values.length > 5"
              >
                <span class="caption">
                  {{ extraAllowedValuesText }}
                </span>
              </div>
            </div>

            <!-- CHECKBOXES OR QUANTITY SELECTION FIELDS WITH VALUES -->
            <div v-else-if="(item.element_type.includes('checkbox')) && item.allowed_values.length > 0">
              <div
                class="checkbox-container"
                v-for="value in shownAllowedValues"
              >
                <div v-if="item.show_as_buttons" class="custom_select">
                  {{ value.name }}
                </div>

                <div v-else>
                  <input type="checkbox">
                  <label
                    :class="{'checked': isSelected(value)}"
                  >{{ value.name }}</label>
                </div>
              </div>
              <div
                v-if="item.allowed_values && (item.allowed_values.length > 5)"
              >
                <span class="caption">
                  {{ extraAllowedValuesText }}
                </span>
              </div>
            </div>

            <div v-else-if="(item.element_type === 'quantity_selection') && item.allowed_values.length > 0">
              <div
                v-for="value in shownAllowedValues"
              >
                <select class="quantity-selection--editor">
                  <option>{{ value.name }}: 0</option>
                </select>
              </div>
              <div
                v-if="item.allowed_values && (item.allowed_values.length > 5)"
              >
                <span class="caption">
                  {{ extraAllowedValuesText }}
                </span>
              </div>
            </div>

            <!-- any other element -->
            <div v-else-if="(item.element_type === 'date')">
              <input
                :value="`19 Aug 2019`"
                :disabled="true"
                type="text"
              />
            </div>

            <div v-else-if="(item.element_type === 'readonly')">
              <input
                type="text"
                v-bind="textBindings"
              />
            </div>

            <div v-else-if="(item.element_type === 'drawing')">
              <div
                class="drawing"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </Draggable>
</template>
<script>
import { Draggable } from "vue-smooth-dnd"
import SensualBadge from '@/components/helpers/DataDisplay/SensualBadge'
import Countries from '@/config/data/countries'
export default {
  data () {
    return {
      countries: {}
    }
  },
  props: {
    item: {
      type: Object,
      required: true
    },
    size: {
      type: String,
      default: 'small'
    },
    setActiveOnClick: {
      type: Boolean,
      default: false
    }
  },
  components: {
    Draggable,
    SensualBadge
  },
  created() {
    let countries = _.orderBy(Countries['de'], ['name'],['asc'])
    this.countries = countries
  },
  computed: {
    defaultValueSelectCountry() {
      if(this.item.default_value) {
        return this.item.default_value
      }
    },
    defaultValueSelect() {
      // We have to show the name of the object that is set as default_value - not the value
      if (this.item.default_value) {
        let val = this.item.allowed_values.find(e => e.value == this.item.default_value)
        if (val) {
          return val.name
        }
      } else {
        return null
      }
    },
    textBindings() {
      if (this.item.default_value) {
        return {
          value: this.item.default_value
        }
      }
      return {
        placeholder: this.item.placeholder
      }
    },
    name() {
      let name = ''
      if (this.item.content) {
        name = this.item.content
      } else {
        name = this.item.label
      }
      if (this.item.required) {
        name += " *"
      }
      return name
    },
    shownAllowedValues() {
      return this.item.allowed_values.slice(0,5)
    },
    extraAllowedValuesText() {
      let sliceAmount = 5
      if (this.item.element_type === 'select') {
        sliceAmount = 1
      }
      return this.$t('ui.drag_editor.extra_values', { num: this.item.allowed_values.length - sliceAmount })
    },
    active() {
      return this.$store.getters.getDragEditorActiveItem === this.item
    },
    dragIcon() {
      return require('@/assets/dragable-smaller.svg')
    },
    classes() {
      let arr = []
      if (this.size === 'small') {
        arr.push('drag-item-small')
      } else if (this.size === 'big') {
        arr.push('drag-item-big')
      }
      if (this.active) {
        arr.push('active')
      }
      return arr
    }
  },
  methods: {
    isSelected(item) {
      if (Array.isArray(this.item.default_value)) {
        return this.item.default_value.includes(item.value)
      } else {
        return this.item.default_value === item.value
      }
    },
    labelClasses(item) {
      if (item.element_type.includes('title')) {
        return ['drag-title']
      }
      return []
    },
    setActive() {
      if (this.setActiveOnClick) {
        this.$store.commit('SET_DRAG_EDITOR_ACTIVE', this.item)
      }
    }
  }
}
</script>
<style lang="sass" scoped>
.drag-title
  font-size: 18px

.drag-item
  cursor: grab
  padding: 9px 15px
  margin-bottom: 10px
  border-radius: 5px
  transition: all .15s ease-out
  background-color: #FFF
  &:active
    border: 2px solid $paper-dark
    cursor: grabbing
  &:hover
    border: 2px solid $paper-dark
  p
    font-size: 15px
    font-weight: $font-regular
    margin-bottom: 0px
    padding-left: 15px
  label
    margin: 0
    width: 100%
    text-overflow: ellipsis
    display: inline-block
    overflow: hidden

.drag-item-small
  border: 2px solid #fff
  background-color: #FFF
  padding: 7px 15px
  display: flex
  &:active
    border: 2px solid $paper-dark
  &:hover
    border: 2px solid $paper-dark

.content-zone
  width: 100%

.drag-zone
  align-items: center
  justify-content: center
  display: flex

label
  min-height: 16px
  margin-bottom: 15px
  vertical-align: sub

.drag-item-big
  border: 2px solid $paper-light
  background-color: #FFF
  padding: 15px 15px
  display: flex
  transition: all .15s ease-out
  .form-container
    padding: 0 15px
    .radio-container
      margin: 15px 0px 15px 0px
      width: 90%
      &:last-child
        margin: 15px 0px 0px 0px
      label
        box-decoration-break: clone
        padding-left: 38px
        font-size: 14px
        font-weight: $font-medium
        color: $ink
        pointer-events: none
        transition: all .15s ease-out
        line-height: 20px
        &::before
          content: ''
          height: 20px
          width: 20px
          border: 1px solid $paper-dark
          background-color: $paper-light
          border-radius: 50%
          position: absolute
          left: 46px
          transition: all .15s ease-out
        &.checked
          &::before
            content: ''
            height: 20px
            width: 20px
            border: 1px solid $sensual
            background-color: $paper-light
          &::after
            content: ''
            width: 10px
            height: 10px
            position: absolute
            background-color: $sensual
            left: 51px
            margin-top: 5px
            border-radius: 50%
    .checkbox-container
      margin: 15px 0px 15px 0px
      width: 90%
      &:last-child
        margin: 15px 0px 0px 0px
      label
        box-decoration-break: clone
        padding-left: 38px
        font-size: 14px
        font-weight: $font-medium
        color: $ink
        pointer-events: none
        transition: all .15s ease-out
        line-height: 20px
        &::before
          content: ''
          height: 20px
          width: 20px
          border: 1px solid $paper-dark
          background-color: $paper-light
          position: absolute
          left: 46px
          transition: all .15s ease-out
        &.checked
          &::before
            content: ''
            height: 20px
            width: 20px
            border: 1px solid $sensual
            background-color: $sensual
            position: absolute
            left: 46px
          &::after
            content: ''
            border: 2px solid white
            outline: none !important
            width: 6px
            height: 12px
            transform: rotate(45deg)
            border-top: 0
            border-left: 0
            position: absolute
            left: 53px
            margin-top: 3px

  input[type=radio]
    display: none
  input[type=checkbox]
    display: none
  input
    width: 100%
    display: flex
    pointer-events: none
    border: 1px solid $paper-dark
    background-color: $paper-light
    height: 40px
    margin-top: 10px
    padding: 0px 15px
    &::placeholder
      color: $paper-dark

  textarea
    width: 100%
    display: flex
    pointer-events: none
    border: 1px solid $paper-dark
    background-color: $paper-light
    margin-top: 10px
    padding: 10px 15px
    height: 80px
    &::placeholder
      color: $paper-dark
  select, .quantity-selection--editor
    width: 100%
    display: flex
    pointer-events: none
    border: 1px solid $paper-dark
    background-color: $paper-light
    margin-top: 10px !important
    padding: 10px 15px
    height: 40px
    border-radius: 0px !important
    border-top-right-radius: 0px !important
    margin-bottom: 0px !important
    &::placeholder
      color: $paper-dark


  .custom_select
    height: 25px
    float: left
    background: $paper-light
    border: 1px solid $paper-dark
    padding: 4px
    margin: 3px 6px 3px 0px

.drag-icon
  pointer-events: none

.active
  border-color: $sensual
  background-color: $sensual-lighter
  transition: all .15s ease-out
  &:hover
    border-color: $sensual

.drawing
  margin-top: 10px
  width: 99.9999%
  height: 200px
  background-color: $paper-light
  border: 1px solid #e6e6e6

</style>



// WEBPACK FOOTER //
// EditorDragItem.vue?20cff07c